import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import toast from "react-hot-toast";

const ProductDetails = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const docRef = doc(db, "products", id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setProduct({ id: docSnap.id, ...docSnap.data() });
        } else {
          setProduct(null);
        }

        // Fetch related products
        const querySnapshot = await getDocs(collection(db, "products"));
        const allProducts = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const filtered = allProducts
          .filter((item) => item.id !== id)
          .slice(0, 3);

        setRelatedProducts(filtered);
      } catch (error) {
        console.error("Error fetching product:", error);
        setProduct(null);
      }
      setLoading(false);
      window.scrollTo(0, 0);
    };

    fetchProduct();
  }, [id]);

  if (loading) {
    return <div className="text-center p-10">Loading product...</div>;
  }

  if (!product) {
    return <div className="text-center p-10">Product not found.</div>;
  }

  return (
    <section className="min-h-screen bg-gradient-to-br from-rose-50 to-white px-4 py-10">
      <div className="max-w-6xl mx-auto bg-white shadow-xl rounded-3xl p-6 flex flex-col md:flex-row gap-6 md:gap-8 mb-16">
        <div className="md:w-1/2">
          <img
            src={product.image}
            alt={product.name}
            className="rounded-2xl w-full h-64 sm:h-full object-cover"
          />
        </div>

        <div className="md:w-1/2">
          <h1 className="text-2xl sm:text-3xl font-semibold text-rose-700 mb-2">
            {product.name}
          </h1>
          <p className="text-gray-600 mb-4 text-sm sm:text-base">
            {product.shortDescription}
          </p>
          <p className="text-xl font-bold text-gray-800 mb-4">₹{product.price}</p>

          <div className="mb-4">
            <label className="block text-gray-700 mb-1 text-sm sm:text-base">Quantity:</label>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}
                className="bg-rose-200 hover:bg-rose-300 px-3 py-1 rounded-full text-rose-800 font-bold"
              >
                −
              </button>
              <span className="text-lg">{quantity}</span>
              <button
                onClick={() => setQuantity((prev) => prev + 1)}
                className="bg-rose-200 hover:bg-rose-300 px-3 py-1 rounded-full text-rose-800 font-bold"
              >
                +
              </button>
            </div>
          </div>

          {product.ingredients?.length > 0 && (
            <>
              <h3 className="text-lg font-semibold text-rose-600 mb-1">Ingredients</h3>
              <ul className="list-disc list-inside text-gray-700 mb-6 text-sm sm:text-base">
                {product.ingredients.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </>
          )}

          <h3 className="text-lg font-semibold text-rose-600 mb-2">Customer Reviews</h3>
          <div className="space-y-3">
            {product.reviews?.length ? (
              product.reviews.map((review, index) => (
                <div
                  key={index}
                  className="bg-rose-50 p-3 rounded-xl shadow-sm border border-rose-100 text-sm"
                >
                  <p className="text-gray-800">
                    <strong>{review.user}</strong>: {review.comment}
                  </p>
                  <p className="text-yellow-500">Rating: {review.rating}/5</p>
                </div>
              ))
            ) : (
              <p className="text-gray-500">No reviews yet.</p>
            )}
          </div>

          <button
            onClick={() => {
              addToCart(product, quantity);
              toast.success(`${product.name} added to cart`);
            }}
            className="mt-6 bg-rose-500 text-white py-2 px-6 rounded-full hover:bg-rose-600 transition text-sm sm:text-base"
          >
            Add to Cart
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-1">
        <h2 className="text-xl sm:text-2xl font-semibold text-rose-700 mb-6 text-center">
          Related Products
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {relatedProducts.map((item) => (
            <div
              key={item.id}
              className="bg-white shadow-md rounded-2xl overflow-hidden hover:shadow-xl transition"
            >
              <Link to={`/product/${item.id}`}>
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-full h-48 sm:h-56 object-cover"
                />
              </Link>
              <div className="p-4">
                <h3 className="text-lg font-semibold text-rose-700">{item.name}</h3>
                <p className="text-gray-600 text-sm mt-1 mb-2">₹{item.price}</p>
                <Link
                  to={`/product/${item.id}`}
                  className="inline-block mt-2 bg-rose-500 hover:bg-rose-600 text-white px-4 py-2 rounded-full text-sm transition"
                >
                  View Product
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProductDetails;
